import { CrFormStep } from '@/components/Event/CrForm/CrFormStep'
import { appendWitnessFeedback } from '@/components/Event/CrForm/witnesses'
import { AttendeeRole } from '@/constants'
import {
  AttendeeFormValues,
  Event,
  EventResult,
  ReporterFeedback,
  ZoomParticipant,
} from '@/types'

// Nothing to map when the proceeding didn't happen. DR guards on `first_skipped_step`.
const NOT_PROCEEDING: EventResult[] = [
  'rescheduled',
  'canceled',
  'canceled_cna',
  'reset',
]

// Armed only once Attendance is done — until then the step's own dropdown already shows
// late joiners. Attendance is event-level, so one flag covers every witness.
export const isMappingArmed = (
  values: ReporterFeedback,
  { isOngoing, witnessIndex }: { isOngoing: boolean; witnessIndex: number },
): boolean => {
  if (!isOngoing) {
    return false
  }

  if (values.steps?.[CrFormStep.ATTENDANCE]?.done !== true) {
    return false
  }

  const result = values.events_feedbacks?.[witnessIndex]?.result
  return !result || !NOT_PROCEEDING.includes(result)
}

// One at a time, like DR: the next poll surfaces the next.
export const findUnmappedParticipant = (
  attendees: AttendeeFormValues[],
  participants: ZoomParticipant[],
): ZoomParticipant | undefined => {
  const mappedIds = new Set(
    attendees.flatMap(attendee =>
      (attendee.zoom_participants ?? [])
        .filter(zoomParticipant => !!zoomParticipant)
        .map(zoomParticipant => zoomParticipant.id),
    ),
  )

  return participants.find(participant => !mappedIds.has(participant.id))
}

// A missing list is deliberately not "left" — it must not close a modal being filled in.
// The query's placeholderData means that is defensive only; an empty list is a real one.
export const hasParticipantLeft = (
  participants: ZoomParticipant[] | undefined,
  participant: ZoomParticipant,
): boolean =>
  !!participants &&
  !participants.some(candidate => candidate.id === participant.id)

// Maps one Zoom participant onto the selected attendees. Selecting an attendee asserts
// they were on the call, so presence flips to Yes. Written to every witness section, not
// just the active one onwards: zoom_coverage validates each witness against the whole
// event's participant list.
export const applyParticipantMapping = ({
  values,
  participant,
  attendeeIds,
  newAttendees = [],
}: {
  values: ReporterFeedback
  participant: ZoomParticipant
  attendeeIds: string[]
  // Attendees created inside the modal, appended before the mapping is applied so the
  // whole confirm lands as one state update.
  newAttendees?: AttendeeFormValues[]
}): ReporterFeedback => {
  if (!attendeeIds.length && !newAttendees.length) {
    return values
  }

  const selected = new Set(attendeeIds)

  return {
    ...values,
    events_feedbacks: values.events_feedbacks.map(feedback => ({
      ...feedback,
      attendees: [
        ...feedback.attendees,
        // Cloned per section, and skipped where appendWitnessFeedback already added them.
        ...newAttendees
          .filter(
            newAttendee =>
              !feedback.attendees.some(
                attendee => attendee.id === newAttendee.id,
              ),
          )
          .map(newAttendee => ({
            ...newAttendee,
            zoom_participants: [],
          })),
      ].map(attendee => {
        if (!attendee.id || !selected.has(attendee.id)) {
          return attendee
        }

        const existing = (attendee.zoom_participants ?? []).filter(
          zoomParticipant => !!zoomParticipant,
        )

        return {
          ...attendee,
          present: true,
          zoom_participants: existing.some(
            zoomParticipant => zoomParticipant.id === participant.id,
          )
            ? existing
            : [...existing, participant],
        }
      }),
    })),
  }
}

// One Confirm as a single value transform. Witness-role additions get a section each via
// appendWitnessFeedback (which also re-opens Attendance, disarming this modal until the
// SA marks it done again), then every new attendee goes through applyParticipantMapping —
// as Other, since a person is only the witness in their own section.
export const applyMappingConfirmation = ({
  values,
  event,
  participant,
  activeWitnessIndex,
  attendeeIds,
  newAttendees,
}: {
  values: ReporterFeedback
  event: Pick<Event, 'is_transcript_order_required'>
  participant: ZoomParticipant
  activeWitnessIndex: number
  attendeeIds: string[]
  newAttendees: AttendeeFormValues[]
}): ReporterFeedback => {
  const withWitnessSections = newAttendees
    .filter(attendee => attendee.role === AttendeeRole.WITNESS)
    .reduce(
      (accumulated, attendee) =>
        appendWitnessFeedback({
          values: accumulated,
          event,
          activeWitnessIndex,
          witnessId: attendee.id as string,
          details: attendee,
        }),
      values,
    )

  const mapped = applyParticipantMapping({
    values: withWitnessSections,
    participant,
    attendeeIds,
    newAttendees: newAttendees.map(attendee =>
      attendee.role === AttendeeRole.WITNESS
        ? { ...attendee, role: AttendeeRole.OTHER }
        : attendee,
    ),
  })

  // An addition the SA unticked keeps present: null. Re-open Attendance so they see it
  // there rather than hitting the error at submit inside a step marked done.
  const hasUntickedAddition = newAttendees.some(
    attendee => !attendee.id || !attendeeIds.includes(attendee.id),
  )

  return hasUntickedAddition
    ? {
        ...mapped,
        steps: {
          ...mapped.steps,
          [CrFormStep.ATTENDANCE]: { expanded: true, done: false },
        },
      }
    : mapped
}
